// Centralized lightweight security utilities.
// These helpers avoid repeating window / URL safety logic across the codebase.

/**
 * Open a URL in a new tab safely (adds noopener & noreferrer) after validating protocol.
 * Silently no-ops if URL looks unsafe.
 */
export function safeWindowOpen(rawUrl: string | URL | null | undefined): void {
  if (typeof window === "undefined" || !rawUrl) return;
  try {
    const url = new URL(rawUrl.toString(), window.location.origin);
    // Allow only http(s), mailto and tg (telegram deep links) protocols for current app needs.
    const allowed = ["http:", "https:", "mailto:", "tg:"];
    if (!allowed.includes(url.protocol)) return;
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  } catch {
    // ignore malformed URL
  }
}

/**
 * Basic CSS color sanitizer. Accepts HEX, rgb/rgba(), hsl/hsla(), or CSS var() tokens.
 * Returns sanitized color or null if it doesn't match a safe pattern.
 */
export function sanitizeCssColor(
  candidate: string | undefined | null,
): string | null {
  if (!candidate) return null;
  const trimmed = candidate.trim();
  const patterns = [
    /^#?[0-9a-fA-F]{3,8}$/,
    /^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/,
    /^rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\)$/,
    /^hsl\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*\)$/,
    /^hsla\(\s*\d{1,3}\s*,\s*\d{1,3}%\s*,\s*\d{1,3}%\s*,\s*(0|1|0?\.\d+)\s*\)$/,
    /^var\(--[a-zA-Z0-9-_]+\)$/,
  ];
  if (patterns.some((p) => p.test(trimmed))) {
    // Normalize to start with # for hex without the hash
    if (/^[0-9a-fA-F]{3,8}$/.test(trimmed)) return "#" + trimmed;
    return trimmed;
  }
  return null;
}
import { useAnalytics } from "@/hooks/useAnalytics";
import { useMemo } from "react";
// REMOVED: ApiErrorHandler is deprecated

/**
 * Frontend security monitoring utilities
 */
export class SecurityMonitor {
  private static analytics: ReturnType<typeof useAnalytics> | null = null;

  static init(analytics: ReturnType<typeof useAnalytics>) {
    this.analytics = analytics;
  }

  /**
   * Track security events from frontend
   */
  static trackSecurityEvent(
    event: string,
    details: Record<string, unknown>,
    severity: "low" | "medium" | "high" = "medium",
  ) {
    if (!this.analytics) {
      console.warn("SecurityMonitor not initialized");
      return;
    }

    // Track with PostHog
    this.analytics.trackEvent("security_event", {
      event_type: event,
      severity,
      ...details,
      timestamp: new Date().toISOString(),
      source: "frontend",
    });

    // Also log to console for development
    console.log(`[SECURITY ${severity.toUpperCase()}]`, {
      event,
      details,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track validation errors from API calls
   */
  static trackValidationError(
    functionName: string,
    validationErrors: Array<{
      path: (string | number)[];
      message: string;
      code: string;
    }>,
  ) {
    this.trackSecurityEvent(
      "frontend_validation_error",
      {
        function_name: functionName,
        validation_errors: validationErrors,
        error_count: validationErrors.length,
      },
      "medium",
    );
  }

  /**
   * Track rate limit hits
   */
  static trackRateLimitHit(
    functionName: string,
    retryAfter: number,
    limit: number,
    remaining: number,
  ) {
    this.trackSecurityEvent(
      "frontend_rate_limit_hit",
      {
        function_name: functionName,
        retry_after: retryAfter,
        limit,
        remaining,
      },
      "medium",
    );
  }

  /**
   * Track authentication failures
   */
  static trackAuthFailure(reason: string, context?: Record<string, unknown>) {
    this.trackSecurityEvent(
      "frontend_auth_failure",
      {
        reason,
        ...context,
      },
      "high",
    );
  }

  /**
   * Track suspicious user activity
   */
  static trackSuspiciousActivity(
    activity: string,
    details: Record<string, unknown>,
  ) {
    this.trackSecurityEvent(
      "frontend_suspicious_activity",
      {
        activity,
        ...details,
      },
      "high",
    );
  }

  /**
   * Track payment-related security events
   */
  static trackPaymentSecurityEvent(
    event: string,
    details: Record<string, unknown>,
  ) {
    this.trackSecurityEvent(
      "frontend_payment_security",
      {
        payment_event: event,
        ...details,
      },
      "high",
    );
  }

  /**
   * Track XSS prevention events (if DOMPurify is used on frontend)
   */
  static trackXSSPrevention(
    originalContent: string,
    sanitizedContent: string,
    context: string,
  ) {
    if (originalContent !== sanitizedContent) {
      this.trackSecurityEvent(
        "frontend_xss_prevention",
        {
          context,
          original_length: originalContent.length,
          sanitized_length: sanitizedContent.length,
          content_changed: true,
        },
        "high",
      );
    }
  }

  /**
   * Track general API errors for security analysis
   */
  static trackAPIError(endpoint: string, error: unknown, statusCode?: number) {
    this.trackSecurityEvent(
      "frontend_api_error",
      {
        endpoint,
        error_message:
          typeof error === "object" && error && "message" in error
            ? String((error as { message?: unknown }).message)
            : "Unknown error",
        status_code: statusCode,
        error_type: (error as object)?.constructor?.name || "Unknown",
      },
      "low",
    );
  }
}

/**
 * Enhanced API error handler with security monitoring
 */
export function handleAPIErrorWithMonitoring(
  functionName: string,
  error: unknown,
): string {
  // Track the error for security monitoring
  SecurityMonitor.trackAPIError(functionName, error);

  // Return formatted error message
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/**
 * Client-side input sanitization with monitoring
 */
export function sanitizeWithMonitoring(input: string, context: string): string {
  // For frontend, we'll use a simple HTML sanitization
  const sanitized = input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");

  // Track if sanitization changed the content
  SecurityMonitor.trackXSSPrevention(input, sanitized, context);

  return sanitized;
}

/**
 * Hook for security monitoring integration
 */
export function useSecurityMonitoring() {
  const analytics = useAnalytics();

  // Initialize only once when analytics becomes available
  if (analytics && !SecurityMonitor["analytics"]) {
    SecurityMonitor.init(analytics);
  }

  // Provide a stable memoized object so contexts using it don't re-run effects
  return useMemo(
    () => ({
      trackSecurityEvent:
        SecurityMonitor.trackSecurityEvent.bind(SecurityMonitor),
      trackValidationError:
        SecurityMonitor.trackValidationError.bind(SecurityMonitor),
      trackRateLimitHit:
        SecurityMonitor.trackRateLimitHit.bind(SecurityMonitor),
      trackAuthFailure: SecurityMonitor.trackAuthFailure.bind(SecurityMonitor),
      trackSuspiciousActivity:
        SecurityMonitor.trackSuspiciousActivity.bind(SecurityMonitor),
      trackPaymentSecurityEvent:
        SecurityMonitor.trackPaymentSecurityEvent.bind(SecurityMonitor),
      handleAPIError: handleAPIErrorWithMonitoring,
      sanitizeInput: sanitizeWithMonitoring,
    }),
    [],
  );
}
